import {observer} from 'mobx-react';
import '../geography-filter.scss';
import './roster-comparison-geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import {isAbortError} from '../../../../../lib/error-status';
import {FilterProps} from '../filter-types';
import RosterComparisonMultiselect from '../filter-types/roster-comparison-multiselect';
import {useDistanceCalcTypeFilter} from '../hooks';
import FilterContainer from '../filter-container';
import NOFilter from '../no-filter';
import GeographyFilterWithGrain from '../geography-filter-with-grain';

type Props = {
    showRosterSelection?: boolean;
};

const RosterComparisonGeographyFilter = observer((props: Props) => {
    const {showRosterSelection = false} = props;
    const noStore = useContext(NetworkContext);
    const comparisonStore = noStore.rosterComparisonStore;

    // Keyed on what still needs loading rather than the primary's roster ID: a route change to
    // another revision resets the store (clearing the options) and sets a primary of the same roster
    const rosterToLoad = comparisonStore.filterOptionsRosterToLoad;

    // Load the primary roster's filter options (its own, not the route roster's, which Network
    // Analysis keeps). The store keeps the selected values they still offer and resets the rest
    useEffect(() => {
        const controller = new AbortController();
        // Retries get their own controllers, tracked so they are cancelled with the effect
        const retryControllers: AbortController[] = [];

        const loadFilterOptions = (abortController: AbortController): void => {
            comparisonStore.loadFilterOptions(abortController).catch((err) => {
                // Ignore abort errors - these are expected when component unmounts or the primary changes
                if (!(err instanceof Error && isAbortError(err))) {
                    noStore.handleApiError(err, 'Failed to Load Geography Filters', () => {
                        const retryController = new AbortController();
                        retryControllers.push(retryController);
                        loadFilterOptions(retryController);
                    });
                }
            });
        };

        loadFilterOptions(controller);

        return () => {
            controller.abort();
            retryControllers.forEach((c) => c.abort());
        };
    }, [noStore, comparisonStore, rosterToLoad]);

    const distanceCalcTypeFilter = useDistanceCalcTypeFilter(
        noStore.distanceCalcType,
        (e: string) => {
            noStore.distanceCalcType = e;
        },
    );

    // The filters list the primary roster's options, so each list refreshes when they (re)load
    const {filterRecords, loadingFilterOptions} = comparisonStore;

    const adequacyStandardFilter: FilterProps = useMemo(() => {
        const data = comparisonStore.getFilterValues('standard_type');
        const onChange = (e: string) => {
            comparisonStore.setComparisonFilter('standard_type', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Adequacy Standard',
            data,
            value: comparisonStore.comparisonFilters.standard_type,
            required: true,
            onChange,
            children,
            spinner: loadingFilterOptions,
            ['data-testid']: 'adequacy-standard-dropdown',
        };
    }, [
        filterRecords,
        loadingFilterOptions,
        comparisonStore.comparisonFilters.standard_type,
    ]);

    const standardVersionFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: comparisonStore.comparisonFilters.standard_type,
        };
        const data = comparisonStore.getFilterValues(
            'standard_version',
            geoFilterArgs,
            '',
            'DESC',
        );
        const onChange = (e: string) => {
            comparisonStore.setComparisonFilter('standard_version', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Standard Version',
            data,
            value: comparisonStore.comparisonFilters.standard_version,
            required: true,
            onChange,
            children,
            spinner: loadingFilterOptions,
            ['data-testid']: 'standard-version-dropdown',
        };
    }, [
        filterRecords,
        loadingFilterOptions,
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
    ]);

    const patientRosterFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: comparisonStore.comparisonFilters.standard_type,
            standard_version: comparisonStore.comparisonFilters.standard_version,
        };
        // Get composite key of patient_roster_id and patient_roster_name
        // Format: "id-name" so we can extract the ID for API calls
        const data = comparisonStore.getFilterValues(
            ['patient_roster_id', 'patient_roster_name'],
            geoFilterArgs,
            '-',
        );
        const onChange = (e: string) => {
            // Extract just the ID part (before the dash)
            const rosterId = e ? e.split('-')[0] : '';
            comparisonStore.setComparisonFilter('patient_roster_id', rosterId);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            // Display only the name part (after the dash)
            (item: any): ReactNode => (item ? item.split('-').slice(1).join('-') : item),
        ];
        return {
            label: 'Patient Roster',
            data,
            value: comparisonStore.comparisonFilters.patient_roster_id
                ? data.find((d) =>
                    d.startsWith(comparisonStore.comparisonFilters.patient_roster_id + '-'),
                ) || ''
                : '',
            required: true,
            onChange,
            children,
            spinner: loadingFilterOptions,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        filterRecords,
        loadingFilterOptions,
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
        comparisonStore.comparisonFilters.patient_roster_id
    ]);

    const stateFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            patient_roster_id: comparisonStore.comparisonFilters.patient_roster_id,
            standard_type: comparisonStore.comparisonFilters.standard_type,
            standard_version: comparisonStore.comparisonFilters.standard_version,
        };
        const data = comparisonStore.getFilterValues(
            'state_additional_info.state',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            comparisonStore.setComparisonFilter('state', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'State',
            data,
            value: comparisonStore.comparisonFilters.state,
            onChange,
            children,
            spinner: loadingFilterOptions,
            placeholder: 'Select a state',
            ['data-testid']: 'state-dropdown',
        };
    }, [
        filterRecords,
        loadingFilterOptions,
        comparisonStore.comparisonFilters.state,
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
        comparisonStore.comparisonFilters.patient_roster_id
    ]);

    // Geography data - fetched based on grain and filters, with the same dependencies as the
    // other panels' County filter (useCountyFilter)
    const geoFilterArgs = useMemo(() => ({
        standard_type: comparisonStore.comparisonFilters.standard_type,
        standard_version: comparisonStore.comparisonFilters.standard_version,
        'state_additional_info.state': comparisonStore.comparisonFilters.state,
        patient_roster_id: comparisonStore.comparisonFilters.patient_roster_id,
    }), [
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
        comparisonStore.comparisonFilters.state,
        comparisonStore.comparisonFilters.patient_roster_id
    ]);

    // filterRecords is a dependency so the lists refresh when the primary roster's options (re)load
    // while the filter values stay the same
    const countyData = useMemo(() =>
        comparisonStore.getFilterValues(
            ['county_additional_info.county_name', 'ssacd'],
            geoFilterArgs,
        ),
    [comparisonStore, geoFilterArgs, filterRecords]
    );

    // NOTE: the geographies-table response does not include ZIP values yet (backend pending),
    // so this list is currently empty and GeographyFilterWithGrain shows a "not available" state
    const zipData = useMemo(() =>
        comparisonStore.getFilterValues(
            'zip',
            geoFilterArgs,
        ),
    [comparisonStore, geoFilterArgs, filterRecords]
    );

    const geographyDisabled =
        !comparisonStore.comparisonFilters.state ||
        !comparisonStore.comparisonFilters.patient_roster_id;

    // Layout order (matching the new design):
    // 1. State
    // 2. Geography grain switcher (County | ZIP) + County or ZIP dropdown
    // 3. Distance Calculation Type, Adequacy Standard, Standard Version, Patient Roster
    const filtersAfterGeography = [
        distanceCalcTypeFilter,
        adequacyStandardFilter,
        standardVersionFilter,
        patientRosterFilter,
    ];

    // Multi-select filters manage their own search; every other filter is searchable
    const renderFilter = (f: FilterProps, key: string) => (
        <div key={key} className='filter-section'>
            <NOFilter {...('enableMultiSelect' in f && f.enableMultiSelect ? f : {...f, searchable: true})} />
        </div>
    );

    // FilterContainer supplies the panel shell; filters are passed as children because the
    // geography section sits between them, which a flat filterConfiguration can't express
    return (
        <FilterContainer
            filterConfiguration={[]}
            headingTag='h2'
            rosterSelectionComponent={showRosterSelection ? <RosterComparisonMultiselect /> : undefined}
        >
            <div className='roster-comparison-geography-filter'>
                {renderFilter(stateFilter, 'state')}

                <div className='filter-section'>
                    <GeographyFilterWithGrain
                        geographyGrain={comparisonStore.geographyGrain}
                        onGeographyGrainChange={(grain) => comparisonStore.setGeographyGrain(grain)}
                        countyValue={comparisonStore.comparisonFilters.county}
                        zipValue={comparisonStore.comparisonFilters.zip}
                        onCountyChange={(value) => comparisonStore.setComparisonFilter('county', value)}
                        onZipChange={(value) => comparisonStore.setComparisonFilter('zip', value)}
                        countyData={countyData}
                        zipData={zipData}
                        loading={loadingFilterOptions}
                        disabled={geographyDisabled}
                    />
                </div>

                {filtersAfterGeography.map((f, i) => renderFilter(f, `after-${i}`))}
            </div>
        </FilterContainer>
    );
});

export default RosterComparisonGeographyFilter;
